import { memo, useContext, useEffect, useMemo, useState } from "react";
import { useI18n } from "i18n";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { useAppDispatch } from "store/hooks";
import { Events, TRACKING_CONTEXT } from "modules/tracking";
import { useSelector } from "react-redux";
import {
  selectInviteeEmail,
  selectPreferredLoginMethods,
  selectUser,
} from "modules/auth/redux/selectors";
import { showPrefilledPreferredLoginMethods } from "modules/auth/redux/authSlice";
import { useHasAnsweredQuestions } from "modules/registration";
import { addErrorNotification } from "modules/notification/redux/notificationSlice";
import { Box } from "@remo-co/ui-core/src/components/Box";
import {
  selectHasPayableTicket,
  selectHasTickets,
  selectIsLoading,
} from "modules/ticketing/redux/selectors";
import { setShowTickets } from "modules/ticketing/redux/slice";
import { checkEmailInvited } from "modules/eventMemberList/eventMember.api";
import { setShowRegisterDialog } from "../redux/eventLandingSlice";
import { useEventContext } from "../hooks/useEventContext";
import { useStyles } from "./styles";

const HelperText = memo(({ text }: { text: string }) => (
  <Container top={5}>
    <Typography variant="subtitle1" color="textSecondary" align="center">
      {text}
    </Typography>
  </Container>
));

interface JoinEventButtonProps {
  loading: boolean;
  onClick: () => void;
  eventFull: boolean;
  isSpeaker: boolean;
  isLobbyEnabled: boolean;
  registered: boolean;
}
const JoinEventButton = ({
  loading,
  onClick,
  eventFull,
  isSpeaker,
  isLobbyEnabled,
  registered,
}: JoinEventButtonProps) => {
  const { t } = useI18n(["eventLanding"]);
  const hasTickets = useSelector(selectHasTickets);
  const isTicketLoading = useSelector(selectIsLoading);
  const hasPayableTicket = useSelector(selectHasPayableTicket);
  const user = useSelector(selectUser);
  const styles = useStyles();

  const joinLabel = useMemo(() => {
    if (isTicketLoading) return "";

    if (!user && hasTickets) return t("eventLanding:get.tickets");

    if (user && !registered && hasPayableTicket) {
      return t("eventLanding:get.tickets");
    }

    // Speakers should be able to bypass the Lobby
    if (isLobbyEnabled && !isSpeaker) {
      return t("eventLanding:join.lobby");
    }

    return t("eventLanding:event.join");
  }, [
    hasPayableTicket,
    hasTickets,
    isLobbyEnabled,
    isSpeaker,
    isTicketLoading,
    registered,
    t,
    user,
  ]);

  return (
    <Container flex column alignItems="flex-start">
      <Button
        fullWidth
        variant="primary"
        color="blue"
        onClick={onClick}
        loading={loading || isTicketLoading}
        data-testid="join-event-button"
        contentWrapperClassName={styles.joinEventButtonWrapper}
      >
        {joinLabel}
      </Button>
      {isSpeaker && <HelperText text={t("eventLanding:event.speaker.join")} />}
      {eventFull && <HelperText text={t("eventLanding:event.full")} />}
    </Container>
  );
};

interface UpcomingEventButtonProps {
  loading: boolean;
  joinLobbyClick: () => void;
  onRegister: () => void;
  registered: boolean;
  isLobbyEnabled: boolean;
  isEventOpen: boolean;
}

const UpcomingEventButtons = ({
  loading,
  joinLobbyClick,
  registered,
  onRegister,
  isLobbyEnabled,
  isEventOpen,
}: UpcomingEventButtonProps) => {
  const { t } = useI18n(["eventLanding"]);
  const user = useSelector(selectUser);
  const isJoinLobbyButtonShown = isLobbyEnabled && isEventOpen;
  const hasPayableTicket = useSelector(selectHasPayableTicket);
  const hasTickets = useSelector(selectHasTickets);
  const isTicketLoading = useSelector(selectIsLoading);
  const styles = useStyles();

  useEffect(() => {
    if (isTicketLoading) return;
    if (!hasTickets) return;

    const url = new URL(window.location.href);
    if (url.searchParams.get("proceedWithRegistration") !== "true") return;

    if (!hasPayableTicket && !registered) {
      if (isJoinLobbyButtonShown) {
        joinLobbyClick();
      } else {
        onRegister();
      }
      url.searchParams.delete("proceedWithRegistration");
      window.history.replaceState({}, "", url.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTicketLoading, hasPayableTicket, isJoinLobbyButtonShown, registered]);

  const buttonLabel = useMemo(() => {
    if (loading || isTicketLoading) return "";

    if (!user && hasTickets) return t("eventLanding:get.tickets");

    if (user && !registered && hasPayableTicket)
      return t("eventLanding:get.tickets");

    if (!registered) return t("eventLanding:save.spot");

    return t("eventLanding:attendance.confirm");
  }, [
    hasPayableTicket,
    hasTickets,
    isTicketLoading,
    user,
    loading,
    registered,
    t,
  ]);

  return (
    <Container flex column alignItems="flex-start">
      {isJoinLobbyButtonShown && (
        <Container flex fullWidth top="sm">
          <Button
            style={{ fontSize: navigator.language === "ja" ? 14 : 16 }}
            data-testid="join-lobby-button"
            fullWidth
            // When the event is upcoming, open (T-20 min), and the lobby enabled, unregistered users should see "Join Lobby", but trigger reg flow.
            onClick={registered ? joinLobbyClick : onRegister}
            variant="primary"
            color="blue"
            loading={loading || isTicketLoading}
          >
            {hasPayableTicket
              ? t("eventLanding:get.tickets")
              : t("eventLanding:join.lobby")}
          </Button>
        </Container>
      )}
      {!isJoinLobbyButtonShown && (
        <Container flex column alignItems="flex-start" top="sm">
          <Button
            data-testid="confirm-attendance-button"
            fullWidth
            disabled={registered}
            onClick={onRegister}
            variant="primary"
            color="blue"
            loading={loading || isTicketLoading}
            contentWrapperClassName={styles.joinEventButtonWrapper}
          >
            {buttonLabel}
          </Button>
          {registered && (
            <HelperText text={t("eventLanding:event.already.registered")} />
          )}
        </Container>
      )}
    </Container>
  );
};

export interface EventDetailsButtonsProps {
  eventId: string;
  amIRegistered: boolean;
  isSpeaker: boolean;
  isManager: boolean;
  isLobbyEnabled: boolean;
  isEventOpen: boolean;
  showJoinEventButton: boolean;
  isEventFull: boolean;
  isPrivate: boolean;
  onJoinAsSpeakerClick(): void;
  onJoinEventClick(): void;
  onJoinLobbyClick(): void;
}

export const EventDetailsButtons = ({
  eventId,
  amIRegistered,
  isSpeaker,
  isManager,
  isLobbyEnabled,
  isEventOpen,
  showJoinEventButton,
  isEventFull,
  isPrivate,
  onJoinAsSpeakerClick,
  onJoinEventClick,
  onJoinLobbyClick,
}: EventDetailsButtonsProps): JSX.Element => {
  const { status: questionStatus } = useHasAnsweredQuestions(eventId);
  const user = useSelector(selectUser);
  const { t } = useI18n("eventLanding");
  const { track } = useContext(TRACKING_CONTEXT);
  const isTicketLoading = useSelector(selectIsLoading);
  const hasTickets = useSelector(selectHasTickets);
  const [loadingInvitationCheck, setLoadingInvitationCheck] = useState(false);
  const dispatch = useAppDispatch();
  const inviteeEmail = useSelector(selectInviteeEmail);
  const { event } = useEventContext();
  const preferredLoginMethods = useSelector(selectPreferredLoginMethods);
  const hasPayableTicket = useSelector(selectHasPayableTicket);
  const styles = useStyles();

  const joinEventClickHandler = async () => {
    if (isTicketLoading) return;

    if (user) {
      if (isPrivate) {
        setLoadingInvitationCheck(true);
        const { isSuccess, data } = await checkEmailInvited(
          eventId,
          user.email,
        );
        setLoadingInvitationCheck(false);
        if (isSuccess && !data.invited && data.isPrivate) {
          dispatch(
            addErrorNotification({
              message: t("you.are.not.invited"),
              position: "tc",
            }),
          );
          return;
        }
      }

      if (!amIRegistered && hasPayableTicket) {
        dispatch(setShowTickets(true));
        return;
      }
    }

    if (isLobbyEnabled) {
      track(Events.JOIN_LOBBY_CLICKED_LANDING_PAGE, {
        eventId,
        isLoggedIn: Boolean(user),
      });
    }

    if (!isLobbyEnabled) {
      track(Events.JOIN_EVENT_CLICKED_LANDING_PAGE, {
        eventId,
        isLoggedIn: Boolean(user),
      });
    }

    if (isSpeaker || isManager) {
      onJoinAsSpeakerClick();

      return;
    }

    onJoinEventClick();
  };

  const handleRegisterClick = async () => {
    if (isTicketLoading || !event) return;

    if (user) {
      if (event.isPrivate) {
        setLoadingInvitationCheck(true);
        const { isSuccess, data } = await checkEmailInvited(
          event.id,
          user.email,
        );
        setLoadingInvitationCheck(false);
        if (isSuccess && !data.invited && data.isPrivate) {
          dispatch(
            addErrorNotification({
              message: t("you.are.not.invited"),
              position: "tc",
            }),
          );
          return;
        }
      }

      if (hasPayableTicket) {
        dispatch(setShowTickets(true));
        return;
      }
    }

    if (inviteeEmail && preferredLoginMethods.length > 1) {
      dispatch(showPrefilledPreferredLoginMethods());
    }

    dispatch(setShowRegisterDialog(true));
  };

  const speakerLogin = () => {
    if (showJoinEventButton) {
      joinEventClickHandler();
      return;
    }

    if (isLobbyEnabled && isEventOpen) {
      if (amIRegistered) {
        onJoinLobbyClick();
      } else {
        handleRegisterClick();
      }
    } else {
      handleRegisterClick();
    }
  };

  return (
    <Box display="flex" flexDirection="column">
      {showJoinEventButton ? (
        <JoinEventButton
          loading={questionStatus !== "success" || loadingInvitationCheck}
          onClick={joinEventClickHandler}
          eventFull={isEventFull}
          isSpeaker={isSpeaker}
          isLobbyEnabled={isLobbyEnabled}
          registered={amIRegistered}
        />
      ) : (
        <UpcomingEventButtons
          loading={questionStatus !== "success" || loadingInvitationCheck}
          joinLobbyClick={onJoinLobbyClick}
          onRegister={handleRegisterClick}
          registered={amIRegistered}
          isLobbyEnabled={isLobbyEnabled}
          isEventOpen={isEventOpen}
        />
      )}
      {!user && !isTicketLoading && hasTickets && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="flex-end"
          style={{ gap: 2 }}
        >
          <Typography>{t("tickets.speaker.login")}</Typography>
          <Button
            color="dark"
            variant="ghost"
            onClick={speakerLogin}
            contentWrapperClassName={styles.clickHereButtonWrapper}
          >
            <Typography className={styles.clickHereText}>
              {t("click.here")}
            </Typography>
          </Button>
        </Box>
      )}
    </Box>
  );
};
